import { CheckCircle2 } from "lucide-react";
import { IMAGES, EMPLOYER_JOURNEY, RECRUITMENT_AREAS } from "../data/site";
import PageHero from "../components/common/PageHero";
import SectionHeading from "../components/common/SectionHeading";
import Reveal from "../components/common/Reveal";
import JourneyStepper from "../components/common/JourneyStepper";
import JobRequest from "../components/intake/JobRequest";
import useSeo from "../hooks/useSeo";

export default function Employers() {
  useSeo({
    title: "For Employers — Request Healthcare Talent",
    description:
      "Hospitals, nursing homes and healthcare organisations partner with Azimi & Co. Vision Recruitment to source well-matched, compliance-aware healthcare professionals. Request talent today.",
    keywords:
      "Healthcare Employers, Request Talent, Nurse Recruitment, Healthcare Assistant Recruitment, Hospital Staffing",
  });
  return (
    <>
      <PageHero
        overline="Hire Talent"
        title="Request talent with confidence"
        description="Hospitals, care providers and organisations across sectors partner with us for a clear, intelligence-driven recruitment process."
      />

      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-border shadow-sm">
              <img src={IMAGES.employersDetail} alt="Healthcare leaders in an employer consultation" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <span className="overline text-xs text-gold">Why Employers Partner With Us</span>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">Quality candidates, delivered in partnership</h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              We take time to understand your organisation, roles and standards — then combine
              specialist expertise with AI Recruitment Intelligence to source well-matched,
              compliance-aware professionals. Every step is transparent, every shortlist is
              deliberate, and every placement is built around the right long-term outcome for
              your team.
            </p>
            <ul className="mt-6 space-y-3">
              {["Dedicated consultation and role briefing", "AI-assisted, human-approved shortlists", "Compliance-aware candidate sourcing", "Support through to a successful placement"].map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-charcoal">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading overline="Employer Journey" title="How it works" description="A structured journey from first conversation to placement." />
          <div className="mt-16"><JourneyStepper steps={EMPLOYER_JOURNEY} accent="gold" /></div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading overline="What We Cover" title="Healthcare roles we recruit" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {RECRUITMENT_AREAS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={(i % 4) * 70}>
                  <div className="h-full rounded-2xl border border-border bg-white p-6 shadow-sm">
                    <Icon className="h-6 w-6 text-gold" />
                    <h4 className="mt-4 font-serif text-lg text-navy">{a.title}</h4>
                    <p className="mt-2 text-sm text-muted-foreground">{a.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="request" className="scroll-mt-24 bg-navy py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center">
            <span className="overline text-xs text-gold-light">Request Talent</span>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-white sm:text-4xl">Tell us who you need</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/70">Share your requirements and a consultant will be in touch to arrange a consultation.</p>
          </div>
          <div className="mt-10 rounded-2xl border border-border bg-white p-6 sm:p-8">
            <JobRequest />
          </div>
        </div>
      </section>
    </>
  );
}
